/*
 * Frontend Lightning (canvas) untuk MCP Fleet Portal.
 *
 * Semua render dilakukan lewat Lightning core (tidak ada DOM component).
 * Canvas di-append manual ke <body> lalu di-scale via CSS agar pas di layar;
 * koordinat pointer dipetakan balik ke koordinat stage (1600x1120).
 *
 * Interaksi (klik + input password) ditangani sendiri lewat window listener
 * karena Lightning core tidak menyediakan pointer/keyboard handler di build ini.
 */
import { Lightning, Launch } from "@lightningjs/sdk";

const { Component, Tools } = Lightning;

/* ------------------------------------------------------------------ *
 * Token desain
 * ------------------------------------------------------------------ */
const W = 1600;
const H = 1120;
const PAD = 40;
const CONTENT_W = W - PAD * 2; // 1520
const GAP = 18;
const CARD_W = (CONTENT_W - GAP) / 2; // default untuk grid dua kolom
const HEADER_H = 82;

const LAY = {
  heroEyebrow: 150,
  heroTitle1: 172,
  heroTitle2: 226,
  syncCaption: 222,
  syncValue: 240,
  summaryY: 292,
  summaryH: 112,
  headingEyebrow: 428,
  headingTitle: 446,
  gridY: 496,
  panelBottom: 1060,
  footerY: 1078,
  panelLeftW: 900,
  panelRightW: 602,
};

const C = {
  bg: 0xff090c0c,
  panel: 0xf2131817,
  card: 0xfa141a18,
  deep: 0xf20e1211,
  track: 0x1affffff,
  line: 0x1fb5d3c5,
  lineSoft: 0x14b5d3c5,
  text: 0xffeef4f0,
  body: 0xffb7c1bc,
  muted: 0xff84918b,
  faint: 0xff606b66,
  green: 0xff7cf7b3,
  greenDim: 0xff1f9d61,
  greenInk: 0xff07100b,
  amber: 0xfff4ca73,
  red: 0xffff7c7c,
  blue: 0xff7db9ff,
};

const F = {
  sans: "Inter",
  semi: "InterSemi",
  bold: "InterBold",
  mono: "Mono",
  monoMedium: "MonoMedium",
};

const STATUS = {
  healthy: { fg: C.green, fill: 0x1f7cf7b3 },
  degraded: { fg: C.amber, fill: 0x1ff4ca73 },
  down: { fg: C.red, fill: 0x1fff7c7c },
  unknown: { fg: C.muted, fill: 0x14ffffff },
};

/* ------------------------------------------------------------------ *
 * Helper
 * ------------------------------------------------------------------ */
const statusMeta = (status) => STATUS[status] ?? STATUS.unknown;

const pillWidth = (text) => Math.max(66, Math.round(text.length * 6.6) + 26);

/** Teks; `w` sengaja dipasang sebelum `text` supaya text texture mewarisi lebar. */
function label(content, opts = {}) {
  const {
    x = 0,
    y = 0,
    w,
    font = F.sans,
    size = 12,
    color = C.text,
    align = "left",
    spacing = 0,
    lineHeight,
    wrap = false,
    lines,
    overflow = "…",
    ref,
  } = opts;

  const style = {
    text: content,
    fontFace: font,
    fontSize: size,
    textColor: color,
    textAlign: align,
  };
  if (spacing) style.letterSpacing = spacing;
  if (lineHeight) style.lineHeight = lineHeight;

  const node = { x, y };
  if (ref) node.ref = ref;
  if (w !== undefined) node.w = w;
  if (wrap) {
    style.wordWrap = true;
    style.wordWrapWidth = w;
    style.maxLines = lines ?? 2;
    style.textOverflow = overflow;
  }
  node.text = style;
  return node;
}

/** Panel/rounded rect. `w`/`h` = ukuran visual final (kompensasi stroke + margin canvas). */
function rounded(w, h, opts = {}) {
  const { radius = 18, stroke = C.line, strokeWidth = 1, fill = C.panel, x = 0, y = 0 } = opts;
  return {
    x,
    y,
    rect: true,
    color: 0xffffffff,
    w,
    h,
    texture: Tools.getRoundRect(w - strokeWidth - 2, h - strokeWidth - 2, radius, strokeWidth, stroke, true, fill),
  };
}

function rect(w, h, color, x = 0, y = 0) {
  return { x, y, rect: true, color, w, h };
}

/** Titik bulat kecil (status dot / live indicator). */
function dot(size, color, x = 0, y = 0) {
  return {
    x,
    y,
    rect: true,
    color: 0xffffffff,
    w: size,
    h: size,
    texture: Tools.getRoundRect(size - 2, size - 2, size / 2, 0, 0, true, color),
  };
}

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index > 2 ? 1 : 0)} ${units[index]}`;
};

const duration = (seconds) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return days ? `${days}d ${hours}h` : hours ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const relative = (date) => {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(date).getTime()) / 1000));
  return seconds < 5 ? "just now" : seconds < 60 ? `${seconds}s ago` : `${Math.floor(seconds / 60)}m ago`;
};

/* ------------------------------------------------------------------ *
 * Komponen dasar
 * ------------------------------------------------------------------ */
class View extends Component {
  _init() {
    this.clicks = [];
  }
  hitTest(x, y) {
    for (let i = this.clicks.length - 1; i >= 0; i -= 1) {
      const region = this.clicks[i];
      if (x >= region.x && x <= region.x + region.w && y >= region.y && y <= region.y + region.h) {
        return region.handler;
      }
    }
    return null;
  }
}

class StatusPill extends Component {
  static _template() {
    return { rect: true, color: 0xffffffff };
  }
  set status(status) {
    const meta = statusMeta(status);
    const text = String(status ?? "unknown").toUpperCase();
    const w = pillWidth(text);
    const h = 24;
    this.patch({ w, h, texture: Tools.getRoundRect(w - 2, h - 2, h / 2, 0, 0, true, meta.fill) });
    // Teks dibuat ulang (bukan di-patch) supaya lebar masuk ke text texture
    // sejak awal; kalau tidak, textAlign "center" tidak akan bekerja.
    this.childList.clear();
    this.childList.add(label(text, { x: 0, y: 6, w, font: F.monoMedium, size: 10, color: meta.fg, align: "center", spacing: 0.9 }));
  }
}

class LatencyChart extends Component {
  static _template() {
    return {
      Bars: {},
      Baseline: rect(0, 1, C.lineSoft, 0, 0),
    };
  }
  set data(value) {
    const { samples = [], w = 0, h = 0 } = value ?? {};
    const bars = this.tag("Bars");
    bars.childList.clear();
    this.patch({ w, h });
    this.tag("Baseline").patch({ y: h, w });

    if (!samples.length || w <= 0) return;
    const values = samples.slice(-40).map((sample) => Math.min(Math.max(sample.latencyMs ?? 0, 0), 2000));
    if (values.length === 1) values.unshift(values[0]);
    const max = Math.max(...values, 30);
    const slot = w / values.length;
    const barW = Math.max(2, Math.floor(slot) - 3);

    values.forEach((value, index) => {
      const barH = Math.max(3, Math.round((value / max) * (h - 6)));
      const isLast = index === values.length - 1;
      const ok = (samples.slice(-40)[index] ?? {}).status !== "down";
      bars.childList.add({
        x: Math.round(index * slot + Math.max(0, (slot - barW) / 2)),
        y: h - barH,
        rect: true,
        color: !ok ? C.red : isLast ? C.green : C.greenDim,
        w: barW,
        h: barH,
      });
    });
  }
}

class MetricRow extends Component {
  static _template() {
    return {
      Label: label("", { font: F.mono, size: 11, color: C.body }),
      Value: label("", { x: 0, y: 0, w: 60, font: F.mono, size: 11, color: C.muted, align: "right" }),
      Track: {},
      Fill: {},
    };
  }
  set data(value) {
    const { title = "", detail = "", ratio = 0, warn = false } = value ?? {};
    this.tag("Label").text = title;
    this.tag("Value").text = detail;
    const width = this._rowW ?? 0;
    this.tag("Track").patch(rounded(width, 5, { radius: 3, strokeWidth: 0, stroke: 0, fill: C.track, y: 22 }));
    this.tag("Fill").patch(rounded(Math.max(2, Math.round(width * Math.max(0, Math.min(1, ratio)))), 5, {
      radius: 3,
      strokeWidth: 0,
      stroke: 0,
      fill: warn ? C.amber : C.green,
      y: 22,
    }));
  }
  set rowW(width) {
    // Hanya x yang digeser; lebar Value tetap 60 sehingga rata kanan tetap akurat.
    this._rowW = width;
    this.patch({ w: width });
    this.tag("Value").patch({ x: Math.max(0, width - 60) });
  }
}

class EventRow extends Component {
  static _template() {
    return {
      Dot: rect(8, 8, C.green, 0, 4),
      Title: label("", { x: 20, font: F.sans, size: 12, color: C.body }),
      Meta: label("", { x: 20, y: 19, font: F.mono, size: 10, color: C.muted }),
      Time: label("", { x: 472, y: 2, w: 78, font: F.mono, size: 10, color: C.muted, align: "right" }),
    };
  }
  set data(value) {
    const { event } = value ?? {};
    const meta = statusMeta(event.to);
    this.tag("Dot").patch(dot(8, meta.fg, 0, 4));
    this.tag("Title").text = `${event.serverName} is ${event.to}`;
    this.tag("Meta").text = event.from ? `Changed from ${event.from}` : "Initial observation";
    this.tag("Time").text = relative(event.at);
  }
}

class Button extends Component {
  static _template() {
    return { Body: {} };
  }
  set data(value) {
    this._config = { text: "", w: 120, h: 34, tone: "ghost", ...(value ?? {}) };
    this._render(this._config.text);
  }
  set labelText(text) {
    this._render(text);
  }
  set dimmed(value) {
    this.patch({ alpha: value ? 0.65 : 1 });
  }
  _render(text) {
    const { w, h, tone } = this._config;
    const primary = tone === "primary";
    const body = this.tag("Body");
    body.childList.clear();
    body.childList.add(rounded(w, h, {
      radius: 10,
      strokeWidth: 1,
      stroke: primary ? 0x00000000 : C.line,
      fill: primary ? C.green : 0x8c131817,
    }));
    body.childList.add(label(text, {
      y: Math.round((h - 16) / 2),
      w,
      font: F.semi,
      size: 13,
      color: primary ? C.greenInk : C.body,
      align: "center",
    }));
  }
}

/* ------------------------------------------------------------------ *
 * Login
 * ------------------------------------------------------------------ */
const LOGIN = { w: 430, h: 452, x: (W - 430) / 2, y: (H - 452) / 2 };

class LoginView extends View {
  static _template() {
    return {
      Card: {
        ...rounded(LOGIN.w, LOGIN.h, { radius: 24, fill: C.deep, stroke: C.line, x: LOGIN.x, y: LOGIN.y }),
        Mark: { x: 44, y: 44 },
        Eyebrow: label("MCP OPERATIONS", { x: 44, y: 104, font: F.mono, size: 11, color: C.green, spacing: 1.9 }),
        Title: label("Fleet Portal", { x: 44, y: 122, font: F.bold, size: 38, color: C.text }),
        Subtitle: label("Secure visibility for every context service.", {
          x: 44, y: 182, w: 342, font: F.sans, size: 12, color: C.muted, wrap: true, lines: 2,
        }),
        FieldLabel: label("Admin password", { x: 44, y: 238, font: F.sans, size: 13, color: C.body }),
        Field: {
          ...rounded(338, 48, { radius: 11, fill: 0xff090d0c, stroke: C.line, x: 44, y: 264 }),
          Value: label("", { x: 14, y: 15, font: F.mono, size: 14, color: C.text }),
          Caret: rect(2, 18, C.green, 14, 16),
        },
        Error: label("", { x: 44, y: 392, w: 342, font: F.sans, size: 13, color: C.red, wrap: true, lines: 2 }),
      },
    };
  }

  _init() {
    super._init();
    this._renderMark();
    this.tag("Card").childList.add({
      type: Button,
      ref: "Submit",
      x: 44,
      y: 330,
      data: { text: "Enter portal  →", w: 338, h: 48, tone: "primary" },
    });
    this.clicks = [
      { x: LOGIN.x + 44, y: LOGIN.y + 330, w: 338, h: 48, handler: () => this.handlers?.submit?.() },
    ];
    this._caretTimer = setInterval(() => {
      const caret = this.tag("Caret");
      caret.visible = !caret.visible;
    }, 550);
  }

  _renderMark() {
    const bars = this.tag("Mark");
    bars.childList.clear();
    [[16, 16], [4, 28], [11, 21]].forEach(([offsetY, height], index) => {
      bars.childList.add(rect(7, height, C.green, index * 12, offsetY));
    });
  }

  setPassword(masked) {
    this.tag("Value").text = masked;
    this.tag("Caret").patch({ x: 14 + masked.length * 8.4, visible: true });
  }

  setError(message) {
    this.tag("Error").text = message ?? "";
  }

  setBusy(busy) {
    this.tag("Submit").labelText = busy ? "Signing in…" : "Enter portal  →";
    this.tag("Submit").dimmed = busy;
  }
}

/* ------------------------------------------------------------------ *
 * Mail account administration
 * ------------------------------------------------------------------ */
class FormField extends Component {
  static _template() {
    return { Body: {} };
  }
  set data(value) {
    this._data = value ?? {};
    const { label: caption = "", value: content = "", w = 420, masked = false, focused = false } = this._data;
    const shown = masked && content ? "•".repeat(Math.min(content.length, 42)) : content || "—";
    const body = this.tag("Body");
    body.childList.clear();
    body.childList.add(label(caption.toUpperCase(), { x: 0, y: 0, font: F.mono, size: 10, color: focused ? C.green : C.muted, spacing: 1.1 }));
    body.childList.add({
      ...rounded(w, 46, { radius: 9, fill: 0xff090d0c, stroke: focused ? C.greenDim : C.line, x: 0, y: 20 }),
      Value: label(shown, { x: 14, y: 14, w: w - 28, font: F.mono, size: 13, color: content ? C.text : C.faint }),
    });
  }
}

const MAIL_FIELDS = [
  { ref: "AccountId", key: "id", label: "Account ID", x: 520, y: 250, w: 300, max: 32 },
  { ref: "DisplayName", key: "displayName", label: "Display name", x: 850, y: 250, w: 670, max: 80 },
  { ref: "ImapHost", key: "host", label: "IMAP host", x: 520, y: 340, w: 620, max: 255 },
  { ref: "ImapPort", key: "port", label: "IMAP port", x: 1180, y: 340, w: 160, max: 5 },
  { ref: "Username", key: "username", label: "IMAP username", x: 520, y: 430, w: 480, max: 320 },
  { ref: "MailPassword", key: "password", label: "IMAP password (kosong = pertahankan)", x: 1040, y: 430, w: 480, max: 1024, masked: true },
  { ref: "SmtpHost", key: "smtpHost", label: "SMTP host", x: 520, y: 520, w: 620, max: 255 },
  { ref: "SmtpPort", key: "smtpPort", label: "SMTP port", x: 1180, y: 520, w: 160, max: 5 },
  { ref: "SmtpUsername", key: "smtpUsername", label: "SMTP username (kosong = IMAP user)", x: 520, y: 610, w: 480, max: 320 },
  { ref: "SmtpPassword", key: "smtpPassword", label: "SMTP password (kosong = pertahankan/IMAP)", x: 1040, y: 610, w: 480, max: 1024, masked: true },
  { ref: "SmtpFrom", key: "smtpFrom", label: "From address (dikunci untuk semua email)", x: 520, y: 700, w: 1000, max: 320 },
];

class MailAdminView extends View {
  static _template() {
    return {
      Background: rect(W, H, C.bg),
      Eyebrow: label("ADMINISTRATION", { x: PAD, y: 106, font: F.mono, size: 11, color: C.green, spacing: 1.9 }),
      Title: label("Mail accounts", { x: PAD, y: 126, font: F.bold, size: 38, color: C.text }),
      Subtitle: label("Manage isolated IMAP readers and optional SMTP senders. Passwords are write-only.", { x: PAD, y: 174, font: F.sans, size: 13, color: C.muted }),
      AccountPanel: { ...rounded(420, 850, { radius: 16, fill: C.panel, stroke: C.line, x: PAD, y: 230 }), List: {} },
      FormPanel: { ...rounded(1060, 850, { radius: 16, fill: C.panel, stroke: C.line, x: 500, y: 230 }) },
      Status: label("", { x: 520, y: 1000, w: 1000, font: F.sans, size: 13, color: C.body, wrap: true, lines: 2 }),
    };
  }

  _init() {
    super._init();
    this.accounts = [];
    this.form = this._emptyForm();
    this.focusIndex = -1;
    this.editingExisting = false;
    this.deleteArmed = false;
    this.busy = false;
    this.childList.add({ type: Button, ref: "Back", x: PAD, y: 24, data: { text: "← Dashboard", w: 138, h: 36, tone: "ghost" } });
    this.childList.add({ type: Button, ref: "NewAccount", x: 284, y: 250, data: { text: "+ New", w: 148, h: 36, tone: "ghost" } });
    for (const field of MAIL_FIELDS) {
      this.childList.add({ type: FormField, ref: field.ref, x: field.x, y: field.y });
    }
    this.childList.add({ type: Button, ref: "Test", x: 520, y: 900, data: { text: "Test connection", w: 174, h: 42, tone: "ghost" } });
    this.childList.add({ type: Button, ref: "Save", x: 712, y: 900, data: { text: "Save & activate", w: 174, h: 42, tone: "primary" } });
    this.childList.add({ type: Button, ref: "Enable", x: 904, y: 900, data: { text: "Enable", w: 130, h: 42, tone: "ghost" } });
    this.childList.add({ type: Button, ref: "Disable", x: 1052, y: 900, data: { text: "Disable", w: 130, h: 42, tone: "ghost" } });
    this.childList.add({ type: Button, ref: "Delete", x: 1200, y: 900, data: { text: "Delete", w: 130, h: 42, tone: "ghost" } });
    this.childList.add(label("IMAP TLS", { x: 520, y: 810, font: F.mono, size: 10, color: C.muted, spacing: 1.1, ref: "TlsLabel" }));
    this.childList.add(label("IMAP CERT", { x: 680, y: 810, font: F.mono, size: 10, color: C.muted, spacing: 1.1, ref: "VerifyLabel" }));
    this.childList.add(label("SMTP SEND", { x: 850, y: 810, font: F.mono, size: 10, color: C.muted, spacing: 1.1, ref: "SmtpEnabledLabel" }));
    this.childList.add(label("SMTP TLS", { x: 1040, y: 810, font: F.mono, size: 10, color: C.muted, spacing: 1.1, ref: "SmtpTlsLabel" }));
    this.childList.add(label("SMTP CERT", { x: 1200, y: 810, font: F.mono, size: 10, color: C.muted, spacing: 1.1, ref: "SmtpVerifyLabel" }));
    this._render();
  }

  _emptyForm() {
    return {
      id: "", displayName: "", host: "", port: "993", username: "", password: "", secure: true, verifyTls: true,
      smtpEnabled: false, smtpHost: "", smtpPort: "465", smtpUsername: "", smtpPassword: "", smtpFrom: "",
      smtpSecure: true, smtpVerifyTls: true,
    };
  }

  async load() {
    this._setStatus("Loading accounts…");
    try {
      const response = await fetch("/api/admin/mail/accounts");
      const body = await response.json();
      if (!response.ok) throw new Error(body.error ?? "Gagal memuat account");
      this.accounts = body.accounts ?? [];
      this._renderList();
      if (this.accounts.length && !this.form.id) this._select(this.accounts[0]);
      else this._render();
      this._setStatus(this.accounts.length ? `${this.accounts.length} account configured` : "Belum ada mail account.");
    } catch (error) {
      this._setStatus(error.message, true);
    }
  }

  _render() {
    for (let index = 0; index < MAIL_FIELDS.length; index += 1) {
      const field = MAIL_FIELDS[index];
      this.tag(field.ref).data = {
        label: field.label,
        value: String(this.form[field.key] ?? ""),
        w: field.w,
        masked: field.masked,
        focused: this.focusIndex === index,
      };
    }
    this.tag("TlsLabel").text = `TLS  ${this.form.secure ? "ON" : "OFF"}`;
    this.tag("VerifyLabel").text = `VERIFY CERT  ${this.form.verifyTls ? "ON" : "OFF"}`;
    this.tag("SmtpEnabledLabel").text = `SMTP SEND  ${this.form.smtpEnabled ? "ON" : "OFF"}`;
    this.tag("SmtpTlsLabel").text = `SMTP TLS  ${this.form.smtpSecure ? "ON" : "OFF"}`;
    this.tag("SmtpVerifyLabel").text = `SMTP CERT  ${this.form.smtpVerifyTls ? "ON" : "OFF"}`;
    this.tag("Delete").labelText = this.deleteArmed ? "Confirm delete" : "Delete";
    this._rebuildClicks();
  }

  _renderList() {
    const list = this.tag("List");
    list.childList.clear();
    list.childList.add(label("CONFIGURED ACCOUNTS", { x: 24, y: 28, font: F.mono, size: 10, color: C.muted, spacing: 1.1 }));
    this.accounts.slice(0, 10).forEach((account, index) => {
      const y = 76 + index * 68;
      const selected = account.id === this.form.id;
      list.childList.add({
        ...rounded(372, 56, { radius: 10, fill: selected ? 0x241f9d61 : C.deep, stroke: selected ? C.greenDim : C.line, x: 24, y }),
        Name: label(account.displayName || account.id, { x: 16, y: 10, w: 260, font: F.semi, size: 13, color: C.text }),
        Meta: label(`${account.id} · ${account.active ? "ACTIVE" : "INACTIVE"}`, { x: 16, y: 31, w: 330, font: F.mono, size: 9, color: account.active ? C.green : C.muted }),
      });
    });
  }

  _rebuildClicks() {
    this.clicks = [
      { x: PAD, y: 24, w: 138, h: 36, handler: () => this.handlers?.back?.() },
      { x: 284, y: 250, w: 148, h: 36, handler: () => this._newAccount() },
      { x: 520, y: 790, w: 140, h: 48, handler: () => { this.form.secure = !this.form.secure; this._render(); } },
      { x: 680, y: 790, w: 150, h: 48, handler: () => { this.form.verifyTls = !this.form.verifyTls; this._render(); } },
      { x: 850, y: 790, w: 170, h: 48, handler: () => { this.form.smtpEnabled = !this.form.smtpEnabled; this._render(); } },
      { x: 1040, y: 790, w: 140, h: 48, handler: () => { this.form.smtpSecure = !this.form.smtpSecure; this._render(); } },
      { x: 1200, y: 790, w: 160, h: 48, handler: () => { this.form.smtpVerifyTls = !this.form.smtpVerifyTls; this._render(); } },
      { x: 520, y: 900, w: 174, h: 42, handler: () => void this._test() },
      { x: 712, y: 900, w: 174, h: 42, handler: () => void this._save() },
      { x: 904, y: 900, w: 130, h: 42, handler: () => void this._action("enable") },
      { x: 1052, y: 900, w: 130, h: 42, handler: () => void this._action("disable") },
      { x: 1200, y: 900, w: 130, h: 42, handler: () => void this._delete() },
    ];
    MAIL_FIELDS.forEach((field, index) => {
      this.clicks.push({ x: field.x, y: field.y + 20, w: field.w, h: 46, handler: () => this._focus(index) });
    });
    this.accounts.slice(0, 10).forEach((account, index) => {
      this.clicks.push({ x: 64, y: 306 + index * 68, w: 372, h: 56, handler: () => this._select(account) });
    });
  }

  _focus(index) {
    if (this.editingExisting && MAIL_FIELDS[index].key === "id") return;
    this.focusIndex = index;
    this.deleteArmed = false;
    this._render();
  }

  _newAccount() {
    this.form = this._emptyForm();
    this.focusIndex = 0;
    this.editingExisting = false;
    this.deleteArmed = false;
    this._setStatus("New account. Isi field lalu Test connection.");
    this._renderList();
    this._render();
  }

  _select(account) {
    this.form = {
      id: account.id,
      displayName: account.displayName ?? "",
      host: account.host ?? "",
      port: String(account.port ?? 993),
      username: account.username ?? "",
      password: "",
      secure: account.secure !== false,
      verifyTls: account.verifyTls !== false,
      smtpEnabled: account.smtpEnabled === true,
      smtpHost: account.smtpHost ?? "",
      smtpPort: String(account.smtpPort ?? 465),
      smtpUsername: account.smtpUsername ?? "",
      smtpPassword: "",
      smtpFrom: account.smtpFrom ?? "",
      smtpSecure: account.smtpSecure !== false,
      smtpVerifyTls: account.smtpVerifyTls !== false,
    };
    this.focusIndex = -1;
    this.editingExisting = true;
    this.deleteArmed = false;
    this._renderList();
    this._render();
    this._setStatus(`${account.toolPrefix} · port ${account.servicePort} · ${account.active ? "active" : "inactive"}`);
  }

  onKey(event) {
    if (event.key === "Escape") { this.handlers?.back?.(); return true; }
    if (event.key === "Tab") {
      event.preventDefault();
      this._focus((this.focusIndex + (event.shiftKey ? MAIL_FIELDS.length - 1 : 1)) % MAIL_FIELDS.length);
      return true;
    }
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) { void this._save(); return true; }
    if (this.focusIndex < 0) return false;
    const field = MAIL_FIELDS[this.focusIndex];
    if (event.key === "Backspace") {
      event.preventDefault();
      this.form[field.key] = String(this.form[field.key] ?? "").slice(0, -1);
      this._render();
      return true;
    }
    if (event.metaKey || event.ctrlKey || event.altKey || event.key.length !== 1) return false;
    event.preventDefault();
    let char = event.key;
    if (field.key === "id") char = char.toLowerCase().replace(/[^a-z0-9_-]/g, "");
    if (field.key === "port" || field.key === "smtpPort") char = char.replace(/\D/g, "");
    const current = String(this.form[field.key] ?? "");
    if (char && current.length < field.max) this.form[field.key] = current + char;
    this._render();
    return true;
  }

  onPaste(text) {
    if (this.focusIndex < 0) return false;
    const field = MAIL_FIELDS[this.focusIndex];
    let value = text.replace(/[\r\n]+$/, "");
    if (field.key === "id") value = value.toLowerCase().replace(/[^a-z0-9_-]/g, "");
    if (field.key === "port" || field.key === "smtpPort") value = value.replace(/\D/g, "");
    this.form[field.key] = `${this.form[field.key] ?? ""}${value}`.slice(0, field.max);
    this._render();
    return true;
  }

  _payload() {
    return { ...this.form, port: Number(this.form.port), smtpPort: Number(this.form.smtpPort), activate: true };
  }

  async _request(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: { "Content-Type": "application/json", ...(options.headers ?? {}) },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error ?? `Request gagal (${response.status})`);
    return body;
  }

  async _run(message, operation) {
    if (this.busy) return;
    this.busy = true;
    this._setStatus(message);
    try { await operation(); } catch (error) { this._setStatus(error.message, true); }
    finally { this.busy = false; }
  }

  async _test() {
    await this._run(`Testing IMAP${this.form.smtpEnabled ? " dan SMTP" : ""}…`, async () => {
      await this._request("/api/admin/mail/test", { method: "POST", body: JSON.stringify(this._payload()) });
      this._setStatus(`Connection successful. IMAP read-only${this.form.smtpEnabled ? " dan SMTP login" : ""} verified.`);
    });
  }

  async _save() {
    await this._run("Saving and activating account…", async () => {
      const account = await this._request("/api/admin/mail/accounts", { method: "POST", body: JSON.stringify(this._payload()) });
      this.form.password = "";
      this.form.smtpPassword = "";
      await this.load();
      const selected = this.accounts.find((item) => item.id === account.id);
      if (selected) this._select(selected);
      this._setStatus("Account saved, service activated, and gateway refreshed.");
    });
  }

  async _action(action) {
    if (!this.form.id || !this.editingExisting) return this._setStatus("Pilih account terlebih dahulu.", true);
    await this._run(`${action === "enable" ? "Enabling" : "Disabling"} account…`, async () => {
      await this._request(`/api/admin/mail/accounts/${this.form.id}/${action}`, { method: "POST", body: "{}" });
      await this.load();
    });
  }

  async _delete() {
    if (!this.form.id || !this.editingExisting) return this._setStatus("Pilih account terlebih dahulu.", true);
    if (!this.deleteArmed) {
      this.deleteArmed = true;
      this._setStatus("Klik Confirm delete. Email di server tidak akan dihapus.", true);
      this._render();
      return;
    }
    const id = this.form.id;
    await this._run("Deleting MCP account configuration…", async () => {
      await this._request(`/api/admin/mail/accounts/${id}`, { method: "DELETE", body: JSON.stringify({ confirm: id }) });
      this._newAccount();
      await this.load();
      this._setStatus("Account configuration deleted. Mailbox content untouched.");
    });
  }

  _setStatus(message, error = false) {
    this.tag("Status").text = message ?? "";
    this.tag("Status").color = error ? C.red : C.body;
  }
}

class DolibarrAdminView extends View {
  static _template() {
    return {
      Background: rect(W, H, C.bg),
      Eyebrow: label("ADMINISTRATION", { x: PAD, y: 106, font: F.mono, size: 11, color: C.green, spacing: 1.9 }),
      Title: label("Dolibarr integration", { x: PAD, y: 126, font: F.bold, size: 38, color: C.text }),
      Subtitle: label("Draft-only quotations and invoices. API key is write-only.", { x: PAD, y: 174, font: F.sans, size: 13, color: C.muted }),
      Panel: { ...rounded(1000, 600, { radius: 16, fill: C.panel, stroke: C.line, x: 300, y: 250 }) },
      State: label("NOT CONFIGURED", { x: 340, y: 292, font: F.mono, size: 11, color: C.muted, spacing: 1.1 }),
      Status: label("", { x: 340, y: 690, w: 920, font: F.sans, size: 13, color: C.body, wrap: true, lines: 3 }),
      Safety: label("Safety: create draft only · no validate · no send · no delete · no payment", { x: 340, y: 792, w: 920, font: F.mono, size: 10, color: C.faint }),
    };
  }

  _init() {
    super._init();
    this.form = { apiUrl: "https://finops.adastra.id/api/index.php", apiKey: "" };
    this.focusIndex = -1;
    this.busy = false;
    this.childList.add({ type: Button, ref: "Back", x: PAD, y: 24, data: { text: "← Dashboard", w: 138, h: 36, tone: "ghost" } });
    this.childList.add({ type: FormField, ref: "ApiUrl", x: 340, y: 350 });
    this.childList.add({ type: FormField, ref: "ApiKey", x: 340, y: 460 });
    this.childList.add({ type: Button, ref: "Test", x: 340, y: 590, data: { text: "Test connection", w: 174, h: 42, tone: "ghost" } });
    this.childList.add({ type: Button, ref: "Save", x: 532, y: 590, data: { text: "Save & activate", w: 174, h: 42, tone: "primary" } });
    this.childList.add({ type: Button, ref: "Enable", x: 724, y: 590, data: { text: "Enable", w: 130, h: 42, tone: "ghost" } });
    this.childList.add({ type: Button, ref: "Disable", x: 872, y: 590, data: { text: "Disable", w: 130, h: 42, tone: "ghost" } });
    this._render();
  }

  async load() {
    this._setStatus("Loading configuration…");
    try {
      const response = await fetch("/api/admin/dolibarr");
      const body = await response.json();
      if (!response.ok) throw new Error(body.error ?? "Gagal memuat konfigurasi");
      this.form.apiUrl = body.apiUrl || this.form.apiUrl;
      this.form.apiKey = "";
      this.tag("State").text = body.configured
        ? `${body.active ? "ACTIVE" : "INACTIVE"} · ${body.toolPrefix}`
        : "NOT CONFIGURED";
      this.tag("State").color = body.active ? C.green : C.muted;
      this._render();
      this._setStatus(body.apiKeyConfigured ? "API key configured. Leave blank to keep it." : "API key belum dikonfigurasi.");
    } catch (error) { this._setStatus(error.message, true); }
  }

  _render() {
    this.tag("ApiUrl").data = { label: "Dolibarr API URL", value: this.form.apiUrl, w: 920, focused: this.focusIndex === 0 };
    this.tag("ApiKey").data = { label: "API key (kosong = pertahankan)", value: this.form.apiKey, w: 920, masked: true, focused: this.focusIndex === 1 };
    this.clicks = [
      { x: PAD, y: 24, w: 138, h: 36, handler: () => this.handlers?.back?.() },
      { x: 340, y: 370, w: 920, h: 46, handler: () => this._focus(0) },
      { x: 340, y: 480, w: 920, h: 46, handler: () => this._focus(1) },
      { x: 340, y: 590, w: 174, h: 42, handler: () => void this._test() },
      { x: 532, y: 590, w: 174, h: 42, handler: () => void this._save() },
      { x: 724, y: 590, w: 130, h: 42, handler: () => void this._toggle("enable") },
      { x: 872, y: 590, w: 130, h: 42, handler: () => void this._toggle("disable") },
    ];
  }

  _focus(index) { this.focusIndex = index; this._render(); }
  onKey(event) {
    if (event.key === "Escape") { this.handlers?.back?.(); return true; }
    if (event.key === "Tab") { event.preventDefault(); this._focus(this.focusIndex === 0 ? 1 : 0); return true; }
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) { void this._save(); return true; }
    if (this.focusIndex < 0) return false;
    const key = this.focusIndex === 0 ? "apiUrl" : "apiKey";
    if (event.key === "Backspace") {
      event.preventDefault(); this.form[key] = this.form[key].slice(0, -1); this._render(); return true;
    }
    if (event.metaKey || event.ctrlKey || event.altKey || event.key.length !== 1) return false;
    event.preventDefault();
    if (this.form[key].length < (key === "apiUrl" ? 500 : 1024)) this.form[key] += event.key;
    this._render(); return true;
  }
  onPaste(text) {
    if (this.focusIndex < 0) return false;
    const key = this.focusIndex === 0 ? "apiUrl" : "apiKey";
    this.form[key] = `${this.form[key]}${text.replace(/[\r\n]+$/, "")}`.slice(0, key === "apiUrl" ? 500 : 1024);
    this._render(); return true;
  }
  async _request(path, options = {}) {
    const response = await fetch(path, { ...options, headers: { "Content-Type": "application/json", ...(options.headers ?? {}) } });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error ?? `Request gagal (${response.status})`);
    return body;
  }
  async _run(message, operation) {
    if (this.busy) return;
    this.busy = true; this._setStatus(message);
    try { await operation(); } catch (error) { this._setStatus(error.message, true); }
    finally { this.busy = false; }
  }
  async _test() {
    await this._run("Testing HTTPS and Dolibarr authentication…", async () => {
      await this._request("/api/admin/dolibarr/test", { method: "POST", body: JSON.stringify(this.form) });
      this._setStatus("Dolibarr connection successful.");
    });
  }
  async _save() {
    await this._run("Saving, starting service, and refreshing gateway…", async () => {
      await this._request("/api/admin/dolibarr", { method: "POST", body: JSON.stringify(this.form) });
      this.form.apiKey = "";
      await this.load();
      this._setStatus("Dolibarr MCP active. Refresh agent catalog to see dolibarr__ tools.");
    });
  }
  async _toggle(action) {
    await this._run(`${action === "enable" ? "Enabling" : "Disabling"} Dolibarr MCP…`, async () => {
      await this._request(`/api/admin/dolibarr/${action}`, { method: "POST", body: "{}" });
      await this.load();
    });
  }
  _setStatus(message, error = false) { this.tag("Status").text = message ?? ""; this.tag("Status").color = error ? C.red : C.body; }
}

/* ------------------------------------------------------------------ *
 * Header + chrome dashboard
 * ------------------------------------------------------------------ */
class HeaderBar extends Component {
  static _template() {
    return {
      Mark: { x: PAD },
      Name: label("MCP Fleet", { x: 81, y: 22, font: F.semi, size: 15, color: C.text }),
      Sub: label("Operations Portal", { x: 81, y: 43, font: F.sans, size: 11, color: C.muted }),
      LiveDot: { ...dot(7, C.green, 1262, 37) },
      // Warna teks dibuat putih lalu di-tint lewat elemen color, supaya bisa
      // berubah hijau/merah tanpa harus membuat text texture baru.
      Live: {
        x: 1278,
        y: 34,
        color: C.green,
        text: { text: "LIVE", fontFace: F.monoMedium, fontSize: 11, textColor: 0xffffffff, letterSpacing: 1.4 },
      },
      Clock: label("--:--:--", { x: 1334, y: 34, w: 90, font: F.mono, size: 11, color: C.muted, align: "right" }),
      Rule: rect(W, 1, C.lineSoft, 0, HEADER_H),
    };
  }
  _init() {
    this.childList.add({
      type: Button,
      ref: "SignOut",
      x: W - PAD - 118,
      y: 24,
      data: { text: "Sign out", w: 118, h: 34, tone: "ghost" },
    });
    [[12, 12], [4, 20], [9, 15]].forEach((entry, index) => {
      const [offsetY, height] = entry;
      this.tag("Mark").childList.add(rect(5, height, C.green, index * 10, 32 + offsetY));
    });
    this._pulse = setInterval(() => {
      const live = this.tag("LiveDot");
      live.setSmooth("alpha", live.alpha > 0.6 ? 0.32 : 1, { duration: 0.82 });
    }, 1000);
  }
  setClock(text) {
    this.tag("Clock").text = text;
  }
  setLive(online) {
    const color = online ? C.green : C.red;
    this.tag("LiveDot").patch(dot(7, color, 1262, 37));
    this.tag("Live").text = online ? "LIVE" : "OFFLINE";
    this.tag("Live").color = color;
  }
}

class SummaryGrid extends Component {
  static _template() {
    return {
      ...rounded(CONTENT_W, LAY.summaryH, { radius: 16, fill: C.panel, stroke: C.line }),
      Sep1: rect(1, LAY.summaryH, C.lineSoft, 380, 0),
      Sep2: rect(1, LAY.summaryH, C.lineSoft, 760, 0),
      Sep3: rect(1, LAY.summaryH, C.lineSoft, 1140, 0),
      C0: { type: SummaryCard, x: 0 },
      C1: { type: SummaryCard, x: 380 },
      C2: { type: SummaryCard, x: 760 },
      C3: { type: SummaryCard, x: 1140 },
    };
  }
  set data(items) {
    (items ?? []).forEach((item, index) => {
      this.tag(`C${index}`).data = item;
    });
  }
}

class SummaryCard extends Component {
  static _template() {
    return {
      Caption: label("", { x: 26, y: 28, font: F.mono, size: 10, color: C.muted, spacing: 1.2 }),
      Value: label("", { x: 26, y: 50, font: F.monoMedium, size: 30, color: C.text, spacing: -1 }),
      Suffix: label("", { x: 26, y: 66, font: F.sans, size: 12, color: C.muted }),
    };
  }
  set data(value) {
    const { caption = "", value: text = "", suffix = "" } = value ?? {};
    this.tag("Caption").text = caption;
    this.tag("Value").text = text;
    this.tag("Suffix").patch({ x: 26 + String(text).length * 18 + 6 });
    this.tag("Suffix").text = suffix;
  }
}

/* ------------------------------------------------------------------ *
 * Server card
 * ------------------------------------------------------------------ */
class ServerCard extends Component {
  static _template() {
    return { Body: {} };
  }
  set data(value) {
    const { server, h, w = CARD_W } = value ?? {};
    const compact = h < 200;
    const body = this.tag("Body");
    body.childList.clear();
    this.patch({ w, h });

    body.childList.add(rounded(w, h, { radius: 16, fill: C.card, stroke: C.line }));

    const iconSize = compact ? 32 : 42;
    const iconY = compact ? 22 : 25;
    body.childList.add(rounded(iconSize, iconSize, { radius: compact ? 8 : 10, fill: 0x147cf7b3, stroke: 0x297cf7b3, x: 26, y: iconY }));
    body.childList.add(label(String(server.id ?? "").slice(0, 3).toUpperCase(), {
      x: 26, y: iconY + (compact ? 9 : 14), w: iconSize, font: F.mono, size: compact ? 11 : 13, color: C.green, align: "center",
    }));
    body.childList.add(label(server.name ?? "", { x: 26 + iconSize + 14, y: iconY + (compact ? 2 : 3), font: F.sans, size: compact ? 15 : 18, color: C.text }));
    body.childList.add(label(server.description ?? "", {
      x: 26 + iconSize + 14, y: iconY + (compact ? 22 : 27), w: w - (26 + iconSize + 14) - 150,
      font: F.sans, size: 12, color: C.muted, wrap: true, lines: 1,
    }));
    body.childList.add({
      type: StatusPill,
      x: w - 26 - pillWidth(String(server.status ?? "").toUpperCase()),
      y: compact ? 20 : 26,
      status: server.status ?? "unknown",
    });

    const statsTop = compact ? 62 : 179;
    const footTop = compact ? 124 : 242;
    const statsH = footTop - statsTop;

    if (!compact) {
      body.childList.add({
        type: LatencyChart,
        x: 26,
        y: 87,
        data: { samples: server.history ?? [], w: w - 52, h: 92 },
      });
    }

    body.childList.add(rect(w, 1, C.lineSoft, 0, statsTop));
    body.childList.add(rect(w, 1, C.lineSoft, 0, footTop));

    const cellW = w / 4;
    const stats = [
      ["LATENCY", `${server.health?.latencyMs ?? "—"} ms`],
      ["HTTP", String(server.health?.httpStatus || "—")],
      ["MEMORY", formatBytes(server.systemd?.memoryBytes)],
      ["RESTARTS", String(server.systemd?.restarts ?? "—")],
    ];
    stats.forEach(([caption, text], index) => {
      if (index > 0) body.childList.add(rect(1, statsH, C.lineSoft, cellW * index, statsTop));
      body.childList.add(label(caption, { x: cellW * index + 20, y: statsTop + (compact ? 16 : 17), font: F.mono, size: 9, color: C.muted, spacing: 0.9 }));
      body.childList.add(label(text, { x: cellW * index + 20, y: statsTop + (compact ? 34 : 35), font: F.monoMedium, size: 13, color: C.text }));
    });

    const configurable = server.id === "dolibarr" || server.id === "mail";
    body.childList.add(label(configurable ? "CLICK TO CONFIGURE  →" : (server.publicEndpoint ?? ""), {
      x: 20, y: footTop + (compact ? 13 : 16), w: Math.max(100, w - 250), font: F.mono, size: 10, color: C.muted, wrap: true, lines: 1,
    }));
    body.childList.add(label(`${server.service} · PID ${server.systemd?.pid || "—"}`, {
      x: w - 230, y: footTop + (compact ? 13 : 16), w: 210, font: F.mono, size: 10, color: C.muted, align: "right",
    }));
  }
}

/* ------------------------------------------------------------------ *
 * Panel bawah: host telemetry + activity
 * ------------------------------------------------------------------ */
class HostPanel extends Component {
  static _template() {
    return {
      Body: {},
      Title: label("Machine telemetry", { x: 26, y: 46, font: F.sans, size: 24, color: C.text }),
      Eyebrow: label("HOST", { x: 26, y: 26, font: F.mono, size: 11, color: C.green, spacing: 1.9 }),
      Hostname: label("—", { x: 26, y: 28, w: LAY.panelLeftW - 52, font: F.mono, size: 11, color: C.muted, align: "right" }),
      Metrics: {},
    };
  }
  set data(value) {
    const { host = {}, h = 0 } = value ?? {};
    this.tag("Hostname").text = host.hostname ?? "—";
    const inner = LAY.panelLeftW - 52;
    const colW = (inner - 24) / 2;
    const row1 = Math.max(96, Math.round(h * 0.42));
    const row2 = row1 + 56;

    const memoryUsed = (host.memoryTotalBytes ?? 0) - (host.memoryFreeBytes ?? 0);
    const diskUsed = (host.diskTotalBytes ?? 0) - (host.diskFreeBytes ?? 0);
    const load = host.loadAverage?.[0] ?? 0;

    const metrics = [
      ["Memory", `${formatBytes(memoryUsed)} / ${formatBytes(host.memoryTotalBytes)}`, memoryUsed / (host.memoryTotalBytes || 1), false],
      ["Disk", `${formatBytes(diskUsed)} / ${formatBytes(host.diskTotalBytes)}`, diskUsed / (host.diskTotalBytes || 1), false],
      ["Load (1m)", load.toFixed(2), load / (host.cpuCount || 1), load / (host.cpuCount || 1) > 0.85],
      ["CPU cores", String(host.cpuCount ?? "—"), 1, false],
    ];

    const container = this.tag("Metrics");
    container.childList.clear();
    metrics.forEach((entry, index) => {
      const [title, detail, ratio, warn] = entry;
      const column = index % 2;
      const row = index < 2 ? row1 : row2;
      container.childList.add({
        type: MetricRow,
        x: 26 + column * (colW + 24),
        y: row,
        rowW: colW,
        data: { title, detail, ratio, warn },
      });
    });
  }
}

class EventsPanel extends Component {
  static _template() {
    return {
      Title: label("Status timeline", { x: 26, y: 46, font: F.sans, size: 24, color: C.text }),
      Eyebrow: label("ACTIVITY", { x: 26, y: 26, font: F.mono, size: 11, color: C.green, spacing: 1.9 }),
      List: {},
      Empty: label("No status changes recorded.", { x: 26, y: 104, font: F.sans, size: 13, color: C.muted }),
    };
  }
  set data(value) {
    const { events = [], h = 0 } = value ?? {};
    const list = this.tag("List");
    list.childList.clear();
    const maxRows = Math.max(1, Math.floor((h - 100 - 14) / 40));
    this.tag("Empty").patch({ visible: events.length === 0 });
    events.slice(0, maxRows).forEach((event, index) => {
      list.childList.add({ type: EventRow, x: 26, y: 100 + index * 40, data: { event } });
    });
  }
}

/* ------------------------------------------------------------------ *
 * Dashboard
 * ------------------------------------------------------------------ */
function layoutFor(count) {
  const columns = count > 4 ? 3 : 2;
  const visible = Math.min(Math.max(count, 1), columns * 2);
  const rows = Math.ceil(visible / columns);
  const cardH = rows === 1 ? 284 : 160;
  const cardW = (CONTENT_W - GAP * (columns - 1)) / columns;
  const gridH = rows * cardH + (rows - 1) * GAP;
  const panelsY = LAY.gridY + gridH + 30;
  return { columns, visible, rows, cardW, cardH, gridH, panelsY, panelsH: LAY.panelBottom - panelsY };
}

class DashboardView extends View {
  static _template() {
    return {
      Hero: {
        Eyebrow: label("SYSTEM OVERVIEW", { x: PAD, y: LAY.heroEyebrow, font: F.mono, size: 11, color: C.green, spacing: 1.9 }),
        Title1: label("Context infrastructure,", { x: PAD, y: LAY.heroTitle1, font: F.bold, size: 44, color: C.text, spacing: -1.6 }),
        Title2: label("at a glance.", { x: PAD, y: LAY.heroTitle2, font: F.sans, size: 44, color: C.muted, spacing: -1.6 }),
        SyncCaption: label("LAST SYNC", { x: 1040, y: LAY.syncCaption, w: 520, font: F.mono, size: 11, color: C.muted, spacing: 1.2, align: "right" }),
        SyncValue: label("—", { x: 1040, y: LAY.syncValue, w: 520, font: F.mono, size: 12, color: C.body, align: "right" }),
      },
      Heading: {
        Eyebrow: label("SERVICES", { x: PAD, y: LAY.headingEyebrow, font: F.mono, size: 11, color: C.green, spacing: 1.9 }),
        Title: label("Connected servers", { x: PAD, y: LAY.headingTitle, font: F.sans, size: 24, color: C.text }),
      },
      Grid: {},
      Panels: {
        Host: { type: HostPanel, ref: "HostPanel", x: PAD, y: 0 },
        Events: { type: EventsPanel, ref: "EventsPanel", x: PAD + LAY.panelLeftW + GAP, y: 0 },
      },
      Footer: {
        Left: label("MCP Fleet Portal · read-only operations view", { x: PAD, y: LAY.footerY, font: F.mono, size: 10, color: C.faint }),
        Uptime: label("Portal uptime —", { x: W - PAD - 520, y: LAY.footerY, w: 520, font: F.mono, size: 10, color: C.faint, align: "right", ref: "FooterUptime" }),
        Rule: rect(CONTENT_W, 1, C.lineSoft, PAD, LAY.footerY - 24),
      },
    };
  }

  _init() {
    super._init();
    this._headerReady = false;
    this._summaryReady = false;

    this.childList.add({
      type: Button,
      ref: "Refresh",
      x: W - PAD - 136,
      y: LAY.headingTitle - 8,
      data: { text: "Refresh now", w: 136, h: 36, tone: "ghost" },
    });
    this.childList.add({
      type: Button,
      ref: "MailAccounts",
      x: W - PAD - 292,
      y: LAY.headingTitle - 8,
      data: { text: "Mail accounts", w: 138, h: 36, tone: "ghost" },
    });
    this.childList.add({
      type: Button,
      ref: "Dolibarr",
      x: W - PAD - 448,
      y: LAY.headingTitle - 8,
      data: { text: "Dolibarr", w: 138, h: 36, tone: "ghost" },
    });

    this.clicks = [
      { x: W - PAD - 136, y: LAY.headingTitle - 8, w: 136, h: 36, handler: () => this.handlers?.refresh?.() },
      { x: W - PAD - 292, y: LAY.headingTitle - 8, w: 138, h: 36, handler: () => this.handlers?.mail?.() },
      { x: W - PAD - 448, y: LAY.headingTitle - 8, w: 138, h: 36, handler: () => this.handlers?.dolibarr?.() },
      { x: W - PAD - 118, y: 24, w: 118, h: 34, handler: () => this.handlers?.logout?.() },
    ];
  }

  setClock(text) {
    // Header baru dibuat saat snapshot pertama masuk.
    const header = this.tag("Header");
    if (header) header.setClock(text);
  }

  setSync(text) {
    this.tag("SyncValue").text = text;
  }

  set data(snapshot) {
    if (!snapshot) return;
    const servers = snapshot.servers ?? [];
    const layout = layoutFor(servers.length);
    this._buildHeader();
    this._buildSummary(servers, snapshot.host ?? {});
    this._buildServers(servers, layout);
    this._buildPanels(snapshot, layout);
    this.tag("FooterUptime").text = `Portal uptime ${duration((Date.now() - new Date(snapshot.portalStartedAt).getTime()) / 1000)}`;
    this.tag("Header").setLive(servers.some((server) => server.status === "healthy"));
    this.setSync(relative(snapshot.generatedAt));
  }

  _buildHeader() {
    if (this._headerReady) return;
    this._headerReady = true;
    this.childList.add({ type: HeaderBar, ref: "Header", x: 0, y: 0 });
  }

  _buildSummary(servers, host) {
    if (!this._summaryReady) {
      this._summaryReady = true;
      this.childList.add({ type: SummaryGrid, ref: "Summary", x: PAD, y: LAY.summaryY });
    }
    const healthy = servers.filter((server) => server.status === "healthy").length;
    const degraded = servers.filter((server) => server.status === "degraded").length;
    const avgLatency = servers.length
      ? Math.round(servers.reduce((sum, server) => sum + (server.health?.latencyMs ?? 0), 0) / servers.length)
      : 0;
    this.tag("Summary").data = [
      { caption: "TOTAL SERVERS", value: String(servers.length) },
      { caption: "HEALTHY", value: String(healthy), suffix: degraded ? `${degraded} degraded` : "all nominal" },
      { caption: "AVG LATENCY", value: String(avgLatency), suffix: "ms" },
      { caption: "HOST UPTIME", value: duration(host.uptimeSeconds ?? 0) },
    ];
  }

  _buildServers(servers, layout) {
    const grid = this.tag("Grid");
    grid.childList.clear();
    this.clicks = this.clicks.filter((target) => !target.serverCard);
    for (let index = 0; index < layout.visible; index += 1) {
      const column = index % layout.columns;
      const row = Math.floor(index / layout.columns);
      const server = servers[index] ?? { id: "—", name: "—", status: "unknown" };
      const x = PAD + column * (layout.cardW + GAP);
      const y = LAY.gridY + row * (layout.cardH + GAP);
      grid.childList.add({
        type: ServerCard,
        ref: `ServerCard${index}`,
        x,
        y,
        data: { server, w: layout.cardW, h: layout.cardH },
      });
      if (server.id === "dolibarr") {
        this.clicks.push({ x, y, w: layout.cardW, h: layout.cardH, serverCard: true, handler: () => this.handlers?.dolibarr?.() });
      } else if (server.id === "mail") {
        this.clicks.push({ x, y, w: layout.cardW, h: layout.cardH, serverCard: true, handler: () => this.handlers?.mail?.() });
      }
    }
  }

  _buildPanels(snapshot, layout) {
    const host = this.tag("HostPanel");
    const events = this.tag("EventsPanel");
    host.patch({
      ...rounded(LAY.panelLeftW, layout.panelsH, { radius: 16, fill: C.panel, stroke: C.line }),
      x: PAD,
      y: layout.panelsY,
    });
    events.patch({
      ...rounded(LAY.panelRightW, layout.panelsH, { radius: 16, fill: C.panel, stroke: C.line }),
      x: PAD + LAY.panelLeftW + GAP,
      y: layout.panelsY,
    });
    host.data = { host: snapshot.host ?? {}, h: layout.panelsH };
    events.data = { events: snapshot.events ?? [], h: layout.panelsH };
  }
}

/* ------------------------------------------------------------------ *
 * Root
 * ------------------------------------------------------------------ */
class PortalApp extends Component {
  static _template() {
    return {
      rect: true,
      color: C.bg,
      w: W,
      h: H,
      Login: { type: LoginView, x: 0, y: 0, visible: false, alpha: 0 },
      Dashboard: { type: DashboardView, x: 0, y: 0, visible: false, alpha: 0 },
      MailAdmin: { type: MailAdminView, x: 0, y: 0, visible: false, alpha: 0 },
      DolibarrAdmin: { type: DolibarrAdminView, x: 0, y: 0, visible: false, alpha: 0 },
    };
  }

  _init() {
    this._activeView = null;
    this._password = "";
    this._busy = false;
    this._pollTimer = null;

    this.tag("Login").handlers = { submit: () => this._submitLogin() };
    this.tag("Dashboard").handlers = {
      refresh: () => this._refresh(),
      logout: () => this._logout(),
      mail: () => this._openMailAdmin(),
      dolibarr: () => this._openDolibarrAdmin(),
    };
    this.tag("MailAdmin").handlers = { back: () => this._showView("dashboard") };
    this.tag("DolibarrAdmin").handlers = { back: () => this._showView("dashboard") };

    window.addEventListener("keydown", (event) => this._onKey(event));
    window.addEventListener("paste", (event) => this._onPaste(event));
    window.addEventListener("mousedown", (event) => this._onPointer(event));

    const tick = () => {
      const now = new Date().toLocaleTimeString("en-GB", { hour12: false });
      this.tag("Dashboard").setClock(now);
    };
    tick();
    setInterval(tick, 1000);

    void this._boot();
  }

  _boot() {
    fetch("/api/session")
      .then((response) => response.json())
      .then(async (session) => {
        if (!session?.authenticated) {
          this._showView("login");
          return;
        }
        await this._refresh();
        this._showView("dashboard");
      })
      .catch(() => this._showView("login"));
  }

  _showView(name) {
    if (this._activeView === name) return;
    this._activeView = name;
    const login = this.tag("Login");
    const dashboard = this.tag("Dashboard");
    const mailAdmin = this.tag("MailAdmin");
    const dolibarrAdmin = this.tag("DolibarrAdmin");
    if (name === "login") {
      this._stopPolling();
      dashboard.patch({ visible: false, alpha: 0 });
      mailAdmin.patch({ visible: false, alpha: 0 });
      dolibarrAdmin.patch({ visible: false, alpha: 0 });
      login.patch({ visible: true });
      login.setSmooth("alpha", 1, { duration: 0.22 });
    } else if (name === "mail") {
      this._stopPolling();
      login.patch({ visible: false, alpha: 0 });
      dashboard.patch({ visible: false, alpha: 0 });
      mailAdmin.patch({ visible: true });
      mailAdmin.setSmooth("alpha", 1, { duration: 0.22 });
    } else if (name === "dolibarr") {
      this._stopPolling();
      login.patch({ visible: false, alpha: 0 });
      dashboard.patch({ visible: false, alpha: 0 });
      mailAdmin.patch({ visible: false, alpha: 0 });
      dolibarrAdmin.patch({ visible: true });
      dolibarrAdmin.setSmooth("alpha", 1, { duration: 0.22 });
    } else {
      login.patch({ visible: false, alpha: 0 });
      mailAdmin.patch({ visible: false, alpha: 0 });
      dolibarrAdmin.patch({ visible: false, alpha: 0 });
      dashboard.patch({ visible: true });
      dashboard.setSmooth("alpha", 1, { duration: 0.22 });
      this._startPolling();
    }
  }

  async _openMailAdmin() {
    this._showView("mail");
    await this.tag("MailAdmin").load();
  }

  async _openDolibarrAdmin() {
    this._showView("dolibarr");
    await this.tag("DolibarrAdmin").load();
  }

  _startPolling() {
    this._stopPolling();
    this._pollTimer = setInterval(() => void this._refresh(), 10_000);
  }

  _stopPolling() {
    if (this._pollTimer) clearInterval(this._pollTimer);
    this._pollTimer = null;
  }

  async _refresh() {
    try {
      const response = await fetch("/api/status");
      if (response.status === 401) {
        this._showView("login");
        return;
      }
      if (!response.ok) throw new Error("Status unavailable");
      const data = await response.json();
      this.tag("Dashboard").data = data;
    } catch {
      if (this._activeView === "dashboard") this.tag("Dashboard").setSync("connection lost");
    }
  }

  async _submitLogin() {
    if (this._busy) return;
    this._busy = true;
    const login = this.tag("Login");
    login.setError("");
    login.setBusy(true);
    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: this._password }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        login.setError(body.error ?? "Login gagal. Coba lagi.");
        return;
      }
      this._password = "";
      login.setPassword("");
      await this._refresh();
      this._showView("dashboard");
    } catch {
      login.setError("Portal tidak dapat dihubungi.");
    } finally {
      this._busy = false;
      login.setBusy(false);
    }
  }

  async _logout() {
    try {
      await fetch("/api/logout", { method: "POST" });
    } catch {
      // tetap tampilkan halaman login
    }
    this._password = "";
    this.tag("Login").setPassword("");
    this.tag("Login").setError("");
    this._showView("login");
  }

  _onKey(event) {
    if (this._activeView === "mail") {
      this.tag("MailAdmin").onKey(event);
      return;
    }
    if (this._activeView === "dolibarr") {
      this.tag("DolibarrAdmin").onKey(event);
      return;
    }
    if (this._activeView !== "login") return;
    if (event.key === "Enter") {
      event.preventDefault();
      void this._submitLogin();
      return;
    }
    if (event.key === "Backspace") {
      event.preventDefault();
      this._password = this._password.slice(0, -1);
      this.tag("Login").setPassword("•".repeat(this._password.length));
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (event.key.length === 1 && this._password.length < 128) {
      this._password += event.key;
      this.tag("Login").setPassword("•".repeat(this._password.length));
    }
  }

  _onPaste(event) {
    const pasted = event.clipboardData?.getData("text")?.replace(/[\r\n]+$/, "") ?? "";
    if (!pasted) return;
    if (this._activeView === "mail") {
      if (this.tag("MailAdmin").onPaste(pasted)) event.preventDefault();
      return;
    }
    if (this._activeView === "dolibarr") {
      if (this.tag("DolibarrAdmin").onPaste(pasted)) event.preventDefault();
      return;
    }
    if (this._activeView !== "login") return;
    event.preventDefault();
    this._password = `${this._password}${pasted}`.slice(0, 128);
    this.tag("Login").setPassword("•".repeat(this._password.length));
  }

  _onPointer(event) {
    if (!["login", "dashboard", "mail", "dolibarr"].includes(this._activeView)) return;
    const view = this._activeView === "login"
      ? this.tag("Login")
      : this._activeView === "mail" ? this.tag("MailAdmin")
        : this._activeView === "dolibarr" ? this.tag("DolibarrAdmin") : this.tag("Dashboard");
    const canvas = this.stage.getCanvas();
    const bounds = canvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const x = ((event.clientX - bounds.left) / bounds.width) * W;
    const y = ((event.clientY - bounds.top) / bounds.height) * H;
    const handler = view.hitTest(x, y);
    if (handler) handler();
  }
}

/* ------------------------------------------------------------------ *
 * Bootstrap
 * ------------------------------------------------------------------ */
const ZOOM_MIN = 0.6;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;
const initialDevicePixelRatio = window.devicePixelRatio || 1;
let stageZoom = 1;

function fitStage(canvas, viewport) {
  const fit = Math.min(window.innerWidth / W, window.innerHeight / H);
  // Browser-menu zoom mengubah devicePixelRatio. Faktor ini mencegah fit-to-window
  // menetralkan zoom bawaan browser.
  const browserZoom = (window.devicePixelRatio || 1) / initialDevicePixelRatio;
  const scale = fit * stageZoom * browserZoom;
  const displayW = Math.round(W * scale);
  const displayH = Math.round(H * scale);
  canvas.style.width = `${displayW}px`;
  canvas.style.height = `${displayH}px`;
  viewport.style.width = `${Math.max(window.innerWidth, displayW)}px`;
  viewport.style.height = `${Math.max(window.innerHeight, displayH)}px`;
}

function setStageZoom(canvas, viewport, value) {
  stageZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(value * 10) / 10));
  fitStage(canvas, viewport);
  requestAnimationFrame(() => {
    window.scrollTo({
      left: Math.max(0, (viewport.scrollWidth - window.innerWidth) / 2),
      top: Math.max(0, (viewport.scrollHeight - window.innerHeight) / 2),
    });
  });
}

function handleZoomKey(event, canvas, viewport) {
  if (!event.ctrlKey && !event.metaKey) return;
  if (event.key === "+" || event.key === "=") {
    event.preventDefault();
    setStageZoom(canvas, viewport, stageZoom + ZOOM_STEP);
  } else if (event.key === "-" || event.key === "_") {
    event.preventDefault();
    setStageZoom(canvas, viewport, stageZoom - ZOOM_STEP);
  } else if (event.key === "0") {
    event.preventDefault();
    setStageZoom(canvas, viewport, 1);
  }
}

async function loadFonts() {
  if (!document.fonts) return;
  const faces = [
    '400 16px "Inter"',
    '600 16px "InterSemi"',
    '700 16px "InterBold"',
    '400 16px "Mono"',
    '500 16px "MonoMedium"',
  ];
  await Promise.all(faces.map((face) => document.fonts.load(face).catch(() => undefined)));
}

async function boot() {
  await loadFonts().catch(() => undefined);
  document.getElementById("boot")?.classList.add("hidden");

  const app = Launch(
    PortalApp,
    {
      // `useImageWorker:false` penting: CSP portal tidak mengizinkan blob worker,
      // dan ImageWorker Lightning selalu dibuat dari blob. (Lightning otomatis
      // memakai renderer canvas2d bila WebGL tidak tersedia.)
      stage: { w: W, h: H, precision: 1, clearColor: C.bg, useImageWorker: false },
      keys: null,
      enablePointer: false,
      debug: false,
    },
    { plugins: {} },
  );

  const canvas = app.stage.getCanvas();
  const viewport = document.getElementById("viewport");
  if (!viewport) throw new Error("Portal viewport tidak ditemukan");
  canvas.id = "stage";
  viewport.appendChild(canvas);
  fitStage(canvas, viewport);
  window.addEventListener("resize", () => fitStage(canvas, viewport));
  window.addEventListener("keydown", (event) => handleZoomKey(event, canvas, viewport));
  window.addEventListener("wheel", (event) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    setStageZoom(canvas, viewport, stageZoom + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
  }, { passive: false });
}

void boot();
